import { useState } from "react";
import {
  useCourses,
  useLearningAssignments,
} from "../../backend/queries/learning";
import {
  Page,
  PageHeader,
  DataView,
  StatusBadge,
  EmptyState,
  ErrorState,
  LoadingState,
} from "../../components/patterns";
import { Button } from "../../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../../components/ui/dialog";
import type { Course } from "../../backend/api/learning";
import { CoursePanel } from "./CoursePanel";

export function MyLearning({ userId }: { userId: string }) {
  const courses = useCourses();
  const assignments = useLearningAssignments(userId);
  const [selected, setSelected] = useState("");
  const items = (courses.data || []).filter(course => assignments.data?.some(item => item.course_id === course.id));
  const assignment = (id: string) =>
    assignments.data?.find((item) => item.course_id === id);
  const done = items.filter((item) => assignment(item.id)?.completed_at).length;
  const course = items.find((item) => item.id === selected);
  return (
    <Page width="content">
      <PageHeader
        title="My learning"
        description="Choose a course, complete its activities, then confirm you’re done."
      />
      {courses.isPending || assignments.isPending ? (
        <LoadingState label="Loading your courses" />
      ) : courses.error || assignments.error ? (
        <ErrorState
          onRetry={() => {
            courses.refetch();
            assignments.refetch();
          }}
        />
      ) : (
        <>
          {items.length > 0 && (
            <p role="status" className="text-sm text-muted-foreground">
              {done} of {items.length} completed
              {done === items.length
                ? " · You’re all caught up."
                : " · Choose an unfinished course below."}
            </p>
          )}
          <DataView<Course>
            caption="Your courses"
            rows={[...items].sort(
              (a, b) =>
                Number(Boolean(assignment(a.id)?.completed_at)) -
                Number(Boolean(assignment(b.id)?.completed_at)),
            )}
            getRowKey={(item) => item.id}
            status="ready"
            columns={[
              {
                id: "title",
                header: "Course",
                primary: true,
                cell: (item) => (
                  <span className="font-medium">{item.title}</span>
                ),
              },
              {
                id: "status",
                header: "Status",
                cell: (item) => (
                  <StatusBadge
                    tone={
                      assignment(item.id)?.completed_at ? "success" : "neutral"
                    }
                  >
                    {assignment(item.id)?.completed_at
                      ? "Completed"
                      : assignment(item.id)?.required
                        ? "Required"
                        : "Optional"}
                  </StatusBadge>
                ),
              },
            ]}
            rowActions={(item) => (
              <Button
                size="sm"
                variant={
                  assignment(item.id)?.completed_at ? "outline" : "default"
                }
                onClick={() => setSelected(item.id)}
              >
                {assignment(item.id)?.completed_at ? "Review" : "Start course"}
              </Button>
            )}
            empty={
              <EmptyState
                title="No courses assigned"
                description="Your training will appear here when assigned."
              />
            }
          />
        </>
      )}
      <Dialog
        open={Boolean(course)}
        onOpenChange={(open) => {
          if (!open) setSelected("");
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Your course</DialogTitle>
            <DialogDescription>
              Follow the steps below. Your completion is saved automatically.
            </DialogDescription>
          </DialogHeader>
          {course && (
            <CoursePanel
              key={course.id}
              course={course}
              admin={false}
              completed={Boolean(assignment(course.id)?.completed_at)}
            />
          )}
        </DialogContent>
      </Dialog>
    </Page>
  );
}
